<script setup lang="ts">
  import { ref, watch, onMounted } from 'vue'
  import gsap from 'gsap'

  const props = defineProps<{
    isOpen: boolean
  }>()

  const emit = defineEmits<{
    (e: 'close'): void
  }>()

  const menuRef = ref<HTMLElement | null>(null)

  watch(
    () => props.isOpen,
    newValue => {
      if (newValue) {
        gsap.fromTo(
          menuRef.value,
          {
            x: '100%',
            opacity: 0,
          },
          {
            x: '0%',
            opacity: 1,
            duration: 0.3,
            ease: 'power2.out',
          },
        )
      } else {
        gsap.to(menuRef.value, {
          x: '100%',
          opacity: 0,
          duration: 0.3,
          ease: 'power2.in',
        })
      }
    },
  )

  const scrollToSection = (sectionId: string) => {
    const section = document.getElementById(sectionId)
    if (section) {
      // Obtener la posición del elemento
      const headerOffset = 64 // altura del header
      const elementPosition = section.getBoundingClientRect().top
      const offsetPosition = elementPosition + window.pageYOffset - headerOffset

      // Scroll suave
      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth',
      })

      // Cerrar el menú móvil
      emit('close')
    }
  }

  // Función para manejar la navegación desde cualquier enlace
  const handleNavigation = (event: Event, sectionId: string) => {
    event.preventDefault()
    scrollToSection(sectionId)
  }

  onMounted(() => {
    // Agregar event listeners a todos los enlaces de navegación
    const navLinks = document.querySelectorAll('a[href^="#"]')
    navLinks.forEach(link => {
      link.addEventListener('click', e => {
        const href = link.getAttribute('href')
        if (href) {
          handleNavigation(e, href.substring(1))
        }
      })
    })
  })
</script>

<template>
  <div v-if="isOpen" class="fixed inset-0 z-50">
    <!-- Overlay -->
    <div class="absolute inset-0 bg-black/50" @click="emit('close')"></div>

    <!-- Menu -->
    <div ref="menuRef" class="absolute right-0 top-0 h-full w-64 bg-white shadow-xl">
      <!-- Header -->
      <div class="flex items-center justify-between p-4 border-b">
        <div class="flex items-center gap-2">
          <img src="/assets/accyon.png" class="w-6 h-6" />
          <h1 class="text-2xl font-serif">ACCYON</h1>
        </div>
        <button class="p-2 hover:bg-gray-100 rounded-full" @click="emit('close')">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            class="h-6 w-6"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </button>
      </div>

      <!-- Navigation -->
      <nav class="p-4">
        <ul class="space-y-4">
          <li>
            <a
              href="#section-hero"
              class="block w-full text-left hover:text-teal-700 transition-colors"
              @click="e => handleNavigation(e, 'section-hero')"
            >
              Inicio
            </a>
          </li>
          <li>
            <a
              href="#section-services"
              class="block w-full text-left hover:text-teal-700 transition-colors"
              @click="e => handleNavigation(e, 'section-services')"
            >
              Servicios
            </a>
          </li>
          <li>
            <a
              href="#section-process"
              class="block w-full text-left hover:text-teal-700 transition-colors"
              @click="e => handleNavigation(e, 'section-process')"
            >
              Proceso
            </a>
          </li>
          <li>
            <a
              href="#section-testimonials"
              class="block w-full text-left hover:text-teal-700 transition-colors"
              @click="e => handleNavigation(e, 'section-testimonials')"
            >
              Testimonios
            </a>
          </li>
          <li>
            <a
              href="#section-jobs"
              class="block w-full text-left hover:text-teal-700 transition-colors"
              @click="e => handleNavigation(e, 'section-jobs')"
            >
              Empleos
            </a>
          </li>
          <li>
            <a
              href="#section-contact"
              class="block w-full text-left hover:text-teal-700 transition-colors"
              @click="e => handleNavigation(e, 'section-contact')"
            >
              Contacto
            </a>
          </li>
        </ul>
      </nav>

      <!-- Login Button -->
      <div class="p-4 border-t">
        <button
          class="w-full border border-teal-700 px-4 py-2 rounded-md text-teal-700 hover:bg-teal-700 hover:text-white transition-colors"
        >
          Entrar
        </button>
      </div>
    </div>
  </div>
</template>
